import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Upload } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const MODES = [
  {
    value: "cold",
    label: "Cold email + LinkedIn",
    blurb: "11-step CFO sequence. Email + LinkedIn invite + 2 voice notes. Best for fresh prospects.",
  },
  {
    value: "linkedin",
    label: "LinkedIn-only",
    blurb: "9-step LinkedIn flow (no email). Use when leads have LinkedIn URLs but no email.",
  },
  {
    value: "nurture",
    label: "Long nurture (6-month)",
    blurb: "1 light-touch email per month. For leads who asked you to circle back later.",
  },
  {
    value: "backfill",
    label: "Backfill existing leads (no new sequence)",
    blurb: "Updates missing phone, LinkedIn URL, company, etc. on leads already in your DB. Matches by email or LinkedIn URL. Never creates new leads or starts a sequence.",
  },
];

export function CsvUploadModal({ onImported }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("cold");
  const [tag, setTag] = useState("");
  const [uploading, setUploading] = useState(false);

  const submit = async () => {
    if (!file) { toast.error("Select a CSV file first"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("mode", mode);
    if (tag.trim()) fd.append("tag", tag.trim().toLowerCase());
    try {
      const { data } = await api.post("/leads-import/csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      let msg;
      if (data.mode === "backfill") {
        const phones = data.fields_filled?.phone || 0;
        msg = `Backfill done · ${data.updated}/${data.matched} updated · ${phones} phones added · ${data.not_found} not in DB`;
      } else {
        msg = `Imported ${data.created} new · ${data.duplicates} duplicates · ${data.enrolled} enrolled in ${data.sequence}`;
      }
      toast.success(msg);
      if (data.errors?.length) toast.error(`${data.errors.length} row error(s): ${data.errors[0]}`);
      setOpen(false); setFile(null); setTag("");
      if (onImported) onImported();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="csv-upload-btn" style={{ borderRadius: 2 }}>
          <Upload className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Upload CSV
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl tracking-tight" style={{ fontWeight: 450 }}>Upload leads from CSV</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="overline">CSV file</Label>
            <Input type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="csv-upload-file" />
            <div className="text-xs text-muted-foreground mt-1">Columns expected (any subset, case-insensitive): <span className="font-mono">email, first_name, last_name, full_name, title, company, linkedin_url, industry, location, website</span></div>
          </div>
          <div>
            <Label className="overline">Pipeline mode</Label>
            <div className="grid grid-cols-1 gap-2 mt-2">
              {MODES.map((m) => (
                <label key={m.value} className={`flex gap-3 p-3 border cursor-pointer ${mode === m.value ? "border-primary bg-primary/5" : "border-border"}`} style={{ borderRadius: 2 }}>
                  <input
                    type="radio" name="csv-mode" value={m.value}
                    checked={mode === m.value}
                    onChange={() => setMode(m.value)}
                    data-testid={`csv-mode-${m.value}`}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-medium text-sm">{m.label}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{m.blurb}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label className="overline">Tag (optional)</Label>
            <Input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="e.g. q3_event_attendees" data-testid="csv-upload-tag" />
            <div className="text-xs text-muted-foreground mt-1">Adds this tag to every imported lead so you can filter them later.</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={uploading || !file} data-testid="csv-upload-submit">{uploading ? "Uploading…" : "Import"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
